<template>
  <div>
    <b-form class="pt-2" @submit.prevent="onSubmit">
      <b-form-group :label="translatedLabel('formProductFamily', 'servicelibrary_Report')"
                    label-for="product-family"
                    class="mb-4"
      >
        <b-form-select
          id="product-family"
          v-model="form.productFamily"
          class="product-family-select"
          :disabled="isLoading"
          required
          :options="productFamilyOptions"
        />
      </b-form-group>

      <b-form-group :label="translatedLabel('formDocPartNumber', 'servicelibrary_Report')"
                    label-for="doc-part-number"
                    class="mb-4"
      >
        <b-form-input id="doc-part-number"
                      v-model="form.manualNumber"
                      :placeholder="translatedLabel('formDocPartNumberPlaceholder', 'servicelibrary_Report')"
                      required
                      :disabled="isLoading"
        />
      </b-form-group>

      <b-form-group :label="translatedLabel('formRevision', 'servicelibrary_Report')" label-for="revision" class="mb-4">
        <b-form-input id="revision"
                      v-model="form.revision"
                      :placeholder="translatedLabel('formRevisionPlaceholder', 'servicelibrary_Report')"
                      required
                      :disabled="isLoading"
        />
      </b-form-group>

      <b-form-group :label="translatedLabel('formPageNumber', 'servicelibrary_Report')" label-for="page-number" class="mb-4">
        <b-form-input id="page-number"
                      v-model="form.pageNr"
                      :placeholder="translatedLabel('formPageNumberPlaceholder', 'servicelibrary_Report')"
                      required
                      min="0"
                      step="1"
                      type="number"
                      :disabled="isLoading"
        />
      </b-form-group>

      <b-form-group :label="translatedLabel('formDescription', 'servicelibrary_Report')" label-for="description" class="mb-4">
        <b-form-textarea id="description"
                         v-model="form.description"
                         :placeholder="translatedLabel('formDescriptionPlaceholder', 'servicelibrary_Report')"
                         required
                         rows="6"
                         :disabled="isLoading"
        />
      </b-form-group>

      <FileUpload :file="form.file"
                  :disabled="isLoading"
                  :file-size-m-b="form.fileSizeMB"
                  :max-size="4"
                  :input-label="translatedLabel('formFileInputLabel', 'servicelibrary_Report')"
                  :file-uploaded-label="translatedLabel('formFileInputUploadedLabel', 'servicelibrary_Report')"
                  :select-file-label="translatedLabel('formFileInputSelect', 'servicelibrary_Report')"
                  :file-limitations-label="translatedLabel('formFileInputLimitations', 'servicelibrary_Report')"
                  :file-to-upload-label="translatedLabel('formFileInputToUpload', 'servicelibrary_Report')"
                  @fileUploaded="handleFileUpload"
                  @clearFile="clearFileInput"
      />

      <div class="d-flex justify-content-end">
        <b-button :disabled="isLoading" type="reset" variant="outline-primary" @click="resetForm">
          {{ translatedLabel('cancel', 'servicelibrary_Report') }}
        </b-button>
        <b-button :disabled="!isFormValid || isLoading"
                  type="submit"
                  variant="primary"
                  class="ml-3 d-flex justify-content-center align-items-center"
        >
          <b-spinner v-if="isLoading" small class="mr-2" />
          {{ isLoading ? translatedLabel('loading', 'servicelibrary_Report') : translatedLabel('submit', 'servicelibrary_Report') }}
        </b-button>
      </div>
    </b-form>
  </div>
</template>

<script>
import FileUpload from '@/components/ui/forms/FileUpload.vue'
import {dpmmService} from '@/services'
export default {
  components: {
    FileUpload
  },
  data() {
    return {
      form: {
        manualNumber: '',
        revision: '',
        pageNr: '',
        description: '',
        file: null,
        fileSizeMB: '',
        productFamily: ''
      },
      isLoading: false
    }
  },
  computed: {
    isFormValid() {
      return this.form.manualNumber && this.form.revision && this.form.pageNr && this.form.description && !this.isFileTooLarge
    },
    isFileTooLarge() {
      return parseFloat(this.form.fileSizeMB) > 4
    },
    productFamilyOptions() {
      const productFamily = this.labels?.serviceLibrary_ReportProductFamily ?? {}
      return Object.keys(productFamily)
        .map(el => ({
          value: productFamily[el],
          text: productFamily[el]
        }))
        .sort((a, b) => a.text.localeCompare(b.text))
    }
  },
  watch: {
    'form.pageNr': function (newValue) {
      if (newValue < 0) {
        this.form.pageNr = 1
      }
    },
    labels: {
      immediate: true,
      handler() {
        if (this.productFamilyOptions.length > 0 && !this.form.productFamily) {
          this.form.productFamily = this.productFamilyOptions[0].value
        }
      }
    }
  },
  mounted() {
    this.$store.commit('servicelibrary/saveServiceLibraryFormSubmitted', false)
    this.$store.commit('servicelibrary/saveServiceLibraryFormError', false)
  },
  methods: {
    async onSubmit() {
      const formData = this.buildFormData()
      try {
        this.isLoading = true
        await dpmmService.reportError(formData)
        this.handleSuccess()
      } catch (error) {
        this.handleError(error)
      }
      this.$store.commit('servicelibrary/saveServiceLibraryFormSubmitted', true)
      this.resetForm()
    },
    buildFormData() {
      const formData = new FormData()
      const fields = ['manualNumber', 'revision', 'pageNr', 'description', 'productFamily']
      fields.forEach(field => formData.append(field, this.form[field]))
      if (this.form.file) {
        formData.append('attachment', this.form.file)
      }
      return formData
    },
    handleSuccess() {
      this.$store.commit('servicelibrary/saveServiceLibraryFormError', false)
    },
    handleError() {
      this.$store.commit('servicelibrary/saveServiceLibraryFormError', true)
    },
    handleFileUpload(file) {
      this.form.file = file
      this.form.fileSizeMB = this.calculateFileSizeInMB(file.size)
    },
    resetForm() {
      this.form = {
        manualNumber: '',
        revision: '',
        pageNr: '',
        description: '',
        file: null,
        fileSizeMB: ''
      }
      this.isLoading = false
      this.$emit('onSubmitCallback')
    },
    calculateFileSizeInMB(sizeInBytes) {
      return (sizeInBytes / (1024 * 1024)).toFixed(2)
    },
    clearFileInput() {
      this.form.file = null
      this.form.fileSizeMB = ''
    }
  }
}
</script>

<style lang="scss" scoped>
.product-family-select {
  &:focus {
    box-shadow: none;
   }
}
</style>
